import React, { useState } from "react";
import {
  Box,
  Card,
  useTheme,
  Modal,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CustomTypography from "../common/form/CustomTypography";
import { ProjectRequest } from "../../../models/Project/Project";
import { observer } from "mobx-react";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import ProjectRequestViewPopup from "./ProjectRequestViewPopup";

interface ProjectRequestTableProps {
  list: ProjectRequest[];
  onApproveOrReject: (requestId: number, status: number) => void;
}

const ProjectRequestTable: React.FC<ProjectRequestTableProps> = ({
  list,
  onApproveOrReject,
}) => {
  const theme = useTheme();
  const { userStore } = useStores();
  const [selectedInfo] = useState<string>("");
  const [isInfoModalOpen, setIsInfoModalOpen] = useState<boolean>(false);
  const [viewPopupRequest, setViewPopupRequest] =
    useState<ProjectRequest | null>(null);
  const [isViewPopupOpen, setIsViewPopupOpen] = useState<boolean>(false);

  const canApproveReject = userStore.hasPermission([
    EmployeeRoles.CREATE_PROJECT,
    EmployeeRoles.EDIT_PROJECT_REQUEST,
  ]);

  return (
    <>
      <Box sx={{ borderRadius: 2 }}>
        <Card
          sx={{
            p: 5,
            boxShadow:
              "rgba(9, 30, 66, 0.25) 0px 1px 1px, rgba(9, 30, 66, 0.13) 0px 0px 1px 1px",
          }}
        >
          <CustomTypography
            variant="heading4"
            fontWeight={700}
            sx={{ mb: 3 }}
          >
            Project Request List
          </CustomTypography>

          {list.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <CustomTypography
                variant="body2"
                fontWeight={700}
                color="rgba(218, 218, 218, 1)"
              >
                NOTHING FOUND!
              </CustomTypography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {list.map((prod, prodIndex) => (
                <Box
                  key={prodIndex}
                  sx={{
                    border: "1px solid #E2E8F0",
                    borderLeft: `3px solid ${theme.palette.primary.main}`,
                    borderRadius: "10px",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    p: "10px 16px",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontSize: "0.72rem",
                      color: "#94A3B8",
                      fontWeight: 700,
                      minWidth: 36,
                    }}
                  >
                    #{prod.id}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 120 }}>
                    <CustomTypography variant="body2" fontWeight={700}>
                      {prod.company_name || "--"}
                    </CustomTypography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        mt: 0.25,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      {prod.brand_name && (
                        <CustomTypography variant="body3" color="#64748B">
                          {prod.brand_name}
                        </CustomTypography>
                      )}
                      {prod.brand_name && prod.requestor_name && (
                        <Box
                          component="span"
                          sx={{ color: "#CBD5E1", fontSize: "0.7rem" }}
                        >
                          ·
                        </Box>
                      )}
                      {prod.requestor_name && (
                        <CustomTypography variant="body3" color="#64748B">
                          {prod.requestor_name}
                        </CustomTypography>
                      )}
                    </Box>
                  </Box>
                  {prod.status_label && (
                    <Chip
                      label={prod.status_label}
                      size="small"
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        height: 22,
                        backgroundColor: theme.palette.primary.main + "18",
                        color: theme.palette.primary.main,
                        border: `1px solid ${theme.palette.primary.main}40`,
                      }}
                    />
                  )}
                  <Tooltip title="View request details">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setViewPopupRequest(prod);
                        setIsViewPopupOpen(true);
                      }}
                      sx={{
                        color: theme.palette.customColors.greyTextColor,
                        "&:hover": {
                          color: theme.palette.primary.main,
                          backgroundColor:
                            theme.palette.customColors.lightGreyColor,
                        },
                      }}
                      aria-label="View request"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}
        </Card>
      </Box>

      <Modal
        open={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
        aria-labelledby="info-modal-title"
        aria-describedby="info-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 600,
            maxHeight: "80vh",
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <CustomTypography variant="heading5" fontWeight={600}>
              Additional Information
            </CustomTypography>
            <IconButton
              onClick={() => setIsInfoModalOpen(false)}
              sx={{
                color: theme.palette.grey[500],
                "&:hover": {
                  color: theme.palette.grey[700],
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              backgroundColor: theme.palette.grey[50],
              p: 2,
              borderRadius: 1,
              border: `1px solid ${theme.palette.grey[200]}`,
              maxHeight: "60vh",
              overflow: "auto",
            }}
          >
            <CustomTypography
              variant="body1"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                lineHeight: 1.6,
              }}
            >
              {selectedInfo}
            </CustomTypography>
          </Box>
        </Box>
      </Modal>

      <ProjectRequestViewPopup
        open={isViewPopupOpen}
        request={viewPopupRequest}
        onClose={() => {
          setIsViewPopupOpen(false);
          setViewPopupRequest(null);
        }}
        onApproveOrReject={onApproveOrReject}
        canApproveReject={canApproveReject}
      />
    </>
  );
};

export default observer(ProjectRequestTable);
